import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import {RootState} from "@/store/RootState";
import responseHandler from "@/plugins/errorHandling";
import {ICourier, ICourierCatalog, ICourierForm} from "@/model/Courier/courierInterfaces";

interface CourierState {
  couriers: ICourier[];
  catalogs: ICourierCatalog[];
  errors: IErrors;
}

const state: CourierState = {
  couriers: [],
  catalogs: [],
  errors: {},
};

const getters: GetterTree<CourierState, RootState> = {
  couriers: (state): ICourier[] => state.couriers,
  catalogs: (state): ICourierCatalog[] => state.catalogs,
  errors: (state) => state.errors,
};

const actions: ActionTree<CourierState, RootState> = {

  // couriers
  fetchCouriers: ({ commit }): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'settings/couriers',
    }),
    commit,
    'couriersLoaded'
  ),
  createCourier: ({ commit }, {courier}: {courier: ICourierForm}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'settings/couriers',
      method: 'POST',
      data: courier,
    }),
    commit,
    'courierSaved',
    'settings.couriers.errors'
  ),
  updateCourier: ({ commit }, {courierId, courier}: {courierId: number, courier: Partial<ICourierForm>}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'settings/couriers/' + courierId,
      method: 'PUT',
      data: courier,
    }),
    commit,
    'courierSaved',
    'settings.couriers.errors'
  ),
  deleteCourier: ({ commit }, courierId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'settings/couriers/' + courierId,
      method: 'DELETE',
    }),
    commit,
    'courierDeleted',
    null,
    false
  ),

  // per country catalog
  fetchCatalogs: ({ commit }, countryId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'settings/courier-catalogs',
      params: {country_id: countryId},
    }),
    commit,
    'courierCatalogsLoaded'
  ),
  createCatalog: ({ commit }, {countryId, courierId}: {countryId: number, courierId: number}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'settings/courier-catalogs',
      method: 'POST',
      data: {
        country_id: countryId,
        courier_id: courierId,
      },
    }),
    commit,
    'courierCatalogSaved',
    'settings.couriers.errors',
    false
  ),
  deleteCatalog: ({ commit }, catalogId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'settings/courier-catalogs/' + catalogId,
      method: 'DELETE',
    }),
    commit,
    'courierCatalogDeleted',
    null,
    false
  ),
  reorderCatalogs: ({ commit }, {countryId, ids}: {countryId: number, ids: number[]}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'settings/courier-catalogs/order',
      method: 'PUT',
      data: {
        country_id: countryId,
        ids,
      },
    }),
    commit,
    'courierCatalogsReordered'
  ),

  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  },
};

const mutations: MutationTree<CourierState> = {
  couriersLoaded(state, payload: ICourier[]) {
    state.couriers = payload;
  },
  courierSaved(state, payload: ICourier) {
    const index = state.couriers.findIndex(each => each.id === payload.id);
    if (index === -1) {
      state.couriers = [...state.couriers, payload];
    }
    else {
      state.couriers.splice(index, 1, payload);
    }
  },
  courierCatalogsLoaded(state, payload: ICourierCatalog[]) {
    state.catalogs = payload;
  },
  courierCatalogsReordered(state, payload: ICourierCatalog[]) {
    state.catalogs = payload;
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
};

const namespaced: boolean = true;

export const courierModule: Module<CourierState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
